/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import type { Database } from "@/lib/supabase/types";

export type EstimateTakeoff =
  Database["public"]["Tables"]["takeoff_data"]["Row"];

export interface EstimateWorkArea {
  work_area_id: string;
  name: string;
  description: string | null;
  work_area_type: string | null;
}

export interface TakeoffImportData {
  mappings: Array<{ sourceColumn: string; targetField: string }>;
  rows: Record<string, string>[];
  fileName: string;
}

export interface TakeoffImportLineItem {
  row_number: number;
  description: string;
  fields: Record<string, never>;
  source: "csv_import";
  source_file: string;
  source_row_number: number;
}

export async function fetchEstimateTakeoffs(
  estimateId: string,
): Promise<EstimateTakeoff[]> {
  const api = createApiClient({ autoRetry429: true });
  const result = await api.get<EstimateTakeoff[]>(
    `/api/takeoff-data?estimate_id=${estimateId}&limit=100`,
  );
  if (isApiFailure(result)) throw new Error("Failed to fetch takeoffs");
  return result.data || [];
}

export async function fetchEstimateWorkAreas(
  estimateId: string,
): Promise<EstimateWorkArea[]> {
  const api = createApiClient({ autoRetry429: true });
  const result = await api.get<EstimateWorkArea[]>(
    `/api/estimates/${estimateId}/work-areas`,
  );
  if (isApiFailure(result)) throw new Error("Failed to fetch work areas");
  return result.data || [];
}

export function transformImportDataToLineItems(
  data: TakeoffImportData,
  existingItemsCount: number,
): TakeoffImportLineItem[] {
  return data.rows.map((row, index) => {
    const fields: Record<string, unknown> = {};

    data.mappings.forEach((mapping) => {
      if (
        mapping.targetField !== "skip" &&
        mapping.targetField !== "description"
      ) {
        fields[mapping.targetField] = row[mapping.sourceColumn];
      }
    });

    const descMapping = data.mappings.find(
      (m) => m.targetField === "description",
    );
    const description =
      (descMapping && row[descMapping.sourceColumn]) ?? `Item ${index + 1}`;

    return {
      row_number: existingItemsCount + index + 1,
      description,
      fields: fields as Record<string, never>,
      source: "csv_import",
      source_file: data.fileName,
      source_row_number: index + 1,
    };
  });
}
